import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEventHandler } from "react";

export function useEscSuppressibleTooltip() {
  const [isSuppressed, setIsSuppressed] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const shouldRestoreFocusRef = useRef(false);

  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
  }, []);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLElement>>((event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    shouldRestoreFocusRef.current = true;
    setIsSuppressed(true);
  }, []);

  useLayoutEffect(() => {
    if (!shouldRestoreFocusRef.current) return;
    triggerRef.current?.focus({ preventScroll: true });
    shouldRestoreFocusRef.current = false;
  }, [isSuppressed]);

  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (!triggerRef.current?.matches(":hover")) return;
      setIsSuppressed(true);
    };

    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, []);

  const resetSuppressed = useCallback(() => {
    setIsSuppressed(false);
  }, []);

  return {
    isSuppressed,
    setTriggerRef,
    onKeyDown,
    resetSuppressed,
  };
}
